import { useEffect, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import { ReactComponent as treatmentIcon } from "../../../assets/icon/add_2.svg";
import { ReactComponent as eventIcon } from "../../../assets/icon/event_operation.svg";
import { ReactComponent as transportIcon } from "../../../assets/icon/fish_1.svg";
import { ReactComponent as harvestIcon } from "../../../assets/icon/harvest.svg";
import { ReactComponent as serviceIcon } from "../../../assets/icon/service_operation.svg";
import { ICON_NAME } from "../../../components/Icon";
import LoadingBox from "../../../components/LoadingBox";
import { datetime } from "../../../util/library/datetime";
import { SiteService } from "../../../util/services/site";
import { selectOpMode } from "../../../util/store/selectors";
import { getStore } from "../../../util/store/store";
import { aWeekMillisecond, fishHealth, operationType } from "../../../util/varibles/constants";
import { firstLetter, formatNumber, showErrorResponse } from "../../../util/varibles/global";
import { TOperation } from "../../../util/varibles/interface";
import DrawChart from "../../LocationPage/Sites/SiteDetail/Chart/DrawChart";
import Legends from "../../LocationPage/Sites/SiteDetail/Chart/Legends";
import { addLiceThreshold, KeyChart, propertiesOfChart, UnitOfChart } from "../../LocationPage/Sites/SiteDetail/constants";
import { getTimeByOpMode } from "../../PlanOperationPage/util/function_operation/constants";
import { IFishgroupSelected } from "../List";
import styles from "./style.module.scss";

const outlineChart = ({ data, color, borderColor, time, format, suffix = "" }: { data: string, color: string, borderColor: string, format: string, time: number, suffix?: string }) => {
    const week = datetime(time).format(format);

    return {
        name: data,
        type: 'scatter',
        data: [[week, 0, data]],
        itemStyle: { color },
        symbolSize: 0,
        label: {
            show: true,
            align: "left",
            borderWidth: 1,
            borderType: "solid",
            borderColor,
            borderRadius: 20,
            padding: [6, 12],
            formatter: function (params: any) {
                return params.data[2]
            },
            color
        },
        tooltip: {
            formatter: () => `<div>${week}</div><div>${suffix} ${data}</div>`
        }
    }
}

const textChart = ({ data, color, time, format, suffix = "" }: { data: string | number, color: string, format: string, time: number, suffix?: string }) => {
    const week = datetime(time).format(format);

    return {
        name: data,
        type: 'scatter',
        data: [[week, 0, data]],
        itemStyle: { color },
        symbolSize: 0,
        label: {
            show: true,
            align: "center",
            formatter: function (params: any) {
                return params.data[2]
            },
            color
        },
        tooltip: {
            formatter: () => `<div>${week}</div><div>${suffix} ${data}</div>`
        }
    }
}

const img = {
    [ICON_NAME.HARVEST]: harvestIcon,
    [ICON_NAME.TREATMENT]: treatmentIcon,
    [ICON_NAME.TRANSPORT]: transportIcon,
    [ICON_NAME.EVENT]: eventIcon,
    [ICON_NAME.SERVICE]: serviceIcon
}

const convertData = ([from, to]: number[], type: any, format: string) => {
    const result = [];
    const max = from === to ? from + aWeekMillisecond : to
    for (let i = from; i <= max; i += aWeekMillisecond) {
        const value = datetime(i).format(format);
        result.push([value, type]);
    }
    return result;
}

interface IFishgroupChart {
    line: any
    area: any
    count: number
    fish_groups: any[]
    operations: any[]
    starvings: {
        starving_days: number
        timestamp: number
    }[]
    fish_health: {
        [key: string]: { from: number, to?: number }[]
    }
    units: any[]
    year_week: number[]
}

interface IProps {
    data: IFishgroupSelected
}

const Chart: React.FC<IProps> = ({ data }) => {
    const originalData = useRef(data);
    const [remove, setRemove] = useState<KeyChart[]>([]);
    const [{ dataChart, loading }, setDataChart] = useState<{ dataChart?: any, loading: boolean }>({ loading: false });

    const [{ chart, opsChart, rowMax }, setChart] = useState<{
        chart: any
        opsChart: any,
        rowMax: number,
    }>({ chart: { yLine: {}, yArea: {}, line: [], area: [], units: [], fish_groups: [], fish_health: [], starvings: [], count: 0 }, opsChart: [], rowMax: 0 });

    useEffect(() => {
        setDataChart(prev => ({ ...prev, loading: false }));
        SiteService.fishGroupChart({ group_id: originalData.current.group_id })
            .then(rs => {
                setDataChart({ dataChart: rs, loading: false })
            })
            .catch(error => {
                showErrorResponse(`Get data of ${originalData.current.group_name} failed`, error);
                setDataChart(prev => ({ ...prev, loading: false }));
            })
    }, []);

    useEffect(() => {
        const generateChart = (dataChart: any) => {
            const opMode = selectOpMode(getStore());
            const { operations, units, fish_groups, year_week } = dataChart as IFishgroupChart;
            let yCount: { [id: string]: number } = {};
            const color = document.body.classList.contains('theme-dark') ? '#D2D6DA' : '#2D3939';
            const opsCount: { [week: string]: number } = {};
            const year = datetime(year_week[0]).weekYear;
            const yearEnd = datetime(year_week[year_week.length - 1]).weekYear;
            const format = year !== yearEnd ? 'YYYY - [W]WW' : '[W]WW';
            const weeks = year_week.map(item => datetime(item).format(format));

            const stateOps = {
                opsChart: operations.map(item => {
                    const { processes = [], operation_type } = item;
                    const { icon } = operationType[operation_type];
                    const week = datetime(getTimeByOpMode[opMode](processes[0])).format(format);
                    const y = yCount[week] || 0;
                    yCount[week] = y + 1;
                    const { [icon!]: Symbol }: any = img;
                    const svgEl = new DOMParser().parseFromString(renderToString(<Symbol />), 'application/xml')
                    const pathEL = svgEl.getElementsByTagName('path')[0];
                    pathEL.setAttribute('style', `fill: ${color}`);
                    const s = new XMLSerializer().serializeToString(svgEl);
                    const path = window.btoa(s);
                    pathEL.setAttribute('style', `fill:#2D3939`);
                    opsCount[week] = (opsCount[week] || 0) + 1;

                    return {
                        name: `#${item.operation_code}`,
                        type: 'scatter',
                        data: [[week, y, item]],
                        symbolSize: 15,
                        symbolKeepAspect: true,
                        symbol: 'image://data:image/svg+xml;base64,' + path,
                        itemStyle: { color },
                        tooltip: {
                            formatter: function (params: any) {
                                const data = params.value[2] as TOperation;
                                const result = [
                                    `${week}`,
                                    `<div class='font-bold flex items-center gap-1 text-[15px]'>
                                    ${new XMLSerializer().serializeToString(svgEl)}
                                    <span class="colorMain">#${data.operation_code}</span> 
                                </div>`
                                ]
                                const starvingDates = data.sub_operations.filter(item => item.starving_days);
                                if (starvingDates.length > 0) {
                                    result.push([
                                        `<div class="colorMain">`,
                                        `<span class="text-comment font-bold text-[13px]">Starving Days: </span>`,
                                        `<span class="text-[15px]">`,
                                        starvingDates.map(item => `${item.unit_id} - ${item.starving_days}`).join(', '),
                                        `</span></div>`
                                    ].join(' '))

                                }
                                return result.join(' ')
                            },
                        }
                    }
                }),
            }
            const yLine: { [id: string]: string } = { [UnitOfChart.Lice]: '0' };
            const yArea: { [id: string]: string } = {};
            const { count, line, area, starvings = [], fish_health = {} } = dataChart as IFishgroupChart;
            const ids = new Set(remove);
            const borderColor = document.body.classList.contains('theme-dark') ? '#606A76' : '#C7C5BD';
            const chart = {
                units: units.map(item => outlineChart({
                    data: item.unit_name,
                    time: item.timestamp,
                    color,
                    borderColor,
                    format,
                    suffix: "Unit"
                })),
                fish_groups: fish_groups.map(item => outlineChart({
                    data: item.group_name,
                    time: item.timestamp,
                    color,
                    borderColor,
                    format,
                    suffix: 'Fish group ID: '
                })),
                fish_health: Object.keys(fish_health).reduce((rs: any[], key) => {
                    const { bg, name } = fishHealth[key];
                    if (!bg)
                        return rs;
                    return [...rs, ...fish_health[key].map(({ from, to }) => ({
                        name,
                        type: 'line',
                        symbol: 'none',
                        data: convertData([from, to || Date.now()], 0, format),
                        yAxisIndex: 0,
                        itemStyle: { color: bg },
                        lineStyle: {
                            width: 10,
                            color: bg
                        },
                        tooltip: {
                            valueFormatter: () => `${datetime(from).format('MMM DD, YYYY')} - ${to ? datetime(to).format('MMM DD, YYYY') : 'Now'}`
                        },
                    }))]
                }, []),
                starvings: starvings.map(item => textChart({
                    data: item.starving_days,
                    time: item.timestamp,
                    color,
                    format,
                    suffix: 'Starving days: '
                })),
                line: Object.keys(line).reduce((rs: any, key) => {
                    if (ids.has(key as KeyChart)) {
                        return rs;
                    }
                    const { name, unit, color, lineColor, zlevel, type } = propertiesOfChart[key] || {};
                    yLine[unit || ''] = `${yLine[unit || ''] || Object.keys(yLine).length}`;
                    const { [key]: statistics }: any = line;
                    return [...rs, {
                        name: name ? name : firstLetter(key.replace(/_/g, ' ')),
                        type,
                        data: unit ? statistics.map((item: number) => item ? (Math.round(item * 1000) / 1000) : item) : [],
                        yAxisIndex: yLine[unit || ''],
                        symbol: !lineColor ? 'emptyCircle' : 'none',
                        itemStyle: {
                            color: color,
                        },
                        lineStyle: {
                            color: lineColor || color
                        },
                        zlevel,
                        tooltip: {
                            valueFormatter: (value = '-') => value === '-' ? value : [formatNumber(value), unit].join(' ')
                        }
                    }];
                }, []),
                area: Object.keys(area).reduce((rs: any, key) => {
                    if (ids.has(key as KeyChart) || area[key].length === 0) {
                        return rs;
                    }
                    const {
                        name = key,
                        color,
                        lineColor
                    } = propertiesOfChart[key] || propertiesOfChart[KeyChart.DiseaseLocal];

                    const { type = 'DiseaseLocal' } = propertiesOfChart[key] || {}
                    yArea[type] = `${yArea[type] || Object.keys(yArea).length}`;
                    const { [key]: statistics }: any = area;
                    statistics.forEach((item: any) => {
                        const { from, to } = item;
                        rs.push({
                            name,
                            type: 'line',
                            symbol: 'none',
                            data: convertData([from, to || Date.now()], Number(yArea[type]), format),
                            yAxisIndex: 0,
                            itemStyle: { color },
                            lineStyle: {
                                width: 10,
                                color: lineColor || color
                            },
                            tooltip: {
                                valueFormatter: () => `${datetime(from).format('MMM DD, YYYY')} - ${to ? datetime(to).format('MMM DD, YYYY') : 'Now'}`
                            },
                        });
                    })
                    return rs
                }, [])
            }
            chart.line.push(addLiceThreshold(count, yLine));
            setChart({
                ...stateOps,
                chart: { yLine, yArea, count, weeks, ...chart },
                rowMax: Math.max(...Object.keys(opsCount).map(key => opsCount[key]))
            });
        };
        if (dataChart)
            generateChart(dataChart);
    }, [dataChart, remove])

    return <div className='relative'>
        <DrawChart id={originalData.current.group_id} className={styles.chart} ops={opsChart} chart={chart} rowMax={rowMax} />
        <Legends data={dataChart} remove={remove} onChange={setRemove} />
        {loading && <LoadingBox loading={loading} level='bg' />}
    </div>
}

export default Chart;